/**
 * The Three Hundred — Judges 6–7 story content.
 * Combat scripts live in missions.ts; this is narrative only.
 */
import type { StageStory } from "../types";

export const GIDEON_CHAPTER = {
  id: "gideon-300",
  title: "The Three Hundred",
  kicker: "Single-player · Proclaim · Judges 6–7",
  lead:
    "From a winepress to a night with three hundred. Gideon asked if God was with them — and God proved it by routing a multitude through the weak.",
  attribution: "Based on Judges 6–7 (KJV).",
  /** Always granted on first clear of the finale (any difficulty). */
  signatureVerseId: "eccl-4-9-10",
  finaleMissionId: "gid-pitchers",
} as const;

export const STORY_WINEPRESS: StageStory = {
  title: "By the Winepress",
  scriptureRef: "Judges 6:11–16",
  lie: "If the Lord be with us, why then is all this befallen us? He has forsaken us.",
  reflection:
    "He asked the question under the winepress. The Lord still called him a mighty man of valour.",
  intro: [
    {
      caption: "Hidden wheat",
      artSrc: "gideon-300/g1-hidden-wheat.webp",
      lines: [
        "Seven years Midian had stripped the land. Israel hid in dens and caves.",
        "Gideon threshed wheat by the winepress — to hide it from the Midianites.",
      ],
    },
    {
      caption: "The question",
      artSrc: "gideon-300/g1-the-question.webp",
      lines: [
        "The angel said, The Lord is with thee, thou mighty man of valour.",
        "Gideon answered: If the Lord be with us, why then is all this befallen us? Where be all his miracles? The Lord hath forsaken us.",
      ],
    },
  ],
  outro: [
    {
      caption: "I have sent thee",
      artSrc: "gideon-300/g1-i-have-sent-thee.webp",
      lines: [
        "Go in this thy might… have not I sent thee?",
        "Surely I will be with thee, and thou shalt smite the Midianites as one man.",
      ],
    },
  ],
};

export const STORY_SHALOM: StageStory = {
  title: "Jehovah-shalom",
  scriptureRef: "Judges 6:17–24",
  lie: "Unless I see a sign, I cannot trust this word.",
  reflection:
    "Fire rose from the rock. Peace was named while Midian still held the land.",
  intro: [
    {
      caption: "Shew me a sign",
      artSrc: "gideon-300/g2-shew-me-a-sign.webp",
      lines: [
        "If now I have found grace in thy sight, then shew me a sign that thou talkest with me.",
        "He prepared a kid and unleavened cakes, and set them under the oak.",
      ],
    },
    {
      caption: "Fire from the rock",
      artSrc: "gideon-300/g2-fire-from-the-rock.webp",
      lines: [
        "The angel touched the flesh and cakes with the staff — fire rose out of the rock and consumed them.",
        "The Lord said, Peace be unto thee; fear not: thou shalt not die. Gideon built an altar and called it Jehovah-shalom.",
      ],
    },
  ],
};

export const STORY_JERUBBAAL: StageStory = {
  title: "Jerubbaal",
  scriptureRef: "Judges 6:25–32",
  lie: "If you obey at night, the city will still destroy you in the morning.",
  reflection:
    "He feared the men of the city — and still tore down Baal. If Baal is a god, let him plead for himself.",
  intro: [
    {
      caption: "That same night",
      artSrc: "gideon-300/g3-that-same-night.webp",
      lines: [
        "Throw down the altar of Baal that thy father hath, and cut down the grove that is by it.",
        "Because he feared his father's household and the men of the city, he did it by night.",
      ],
    },
    {
      caption: "Let Baal plead",
      artSrc: "gideon-300/g3-let-baal-plead.webp",
      lines: [
        "In the morning the city cried for Gideon's death.",
        "Joash said, Will ye plead for Baal? If he be a god, let him plead for himself. That day they called him Jerubbaal.",
      ],
    },
  ],
};

export const STORY_JEZREEL: StageStory = {
  title: "Valley of Jezreel",
  scriptureRef: "Judges 6:33–35",
  lie: "The host is without number. You cannot stand in the open.",
  reflection:
    "The Spirit of the Lord came upon Gideon. He blew a trumpet — and the people gathered.",
  intro: [
    {
      caption: "Midian gathers",
      artSrc: "gideon-300/g4-midian-gathers.webp",
      lines: [
        "All the Midianites and the Amalekites and the children of the east pitched in the valley of Jezreel.",
        "They came as grasshoppers for multitude — camels without number.",
      ],
    },
    {
      caption: "The Spirit and the trumpet",
      artSrc: "gideon-300/g4-spirit-trumpet.webp",
      lines: [
        "But the Spirit of the Lord came upon Gideon, and he blew a trumpet; and Abiezer was gathered after him.",
        "Messengers went through Manasseh, Asher, Zebulun, and Naphtali — and they came up to meet them.",
      ],
    },
  ],
};

export const STORY_FLEECE: StageStory = {
  title: "The Fleece",
  scriptureRef: "Judges 6:36–40",
  lie: "If thou wilt save Israel by mine hand — prove it. Then prove it again.",
  reflection:
    "He asked twice. God answered twice. Grace does not mock a trembling servant.",
  intro: [
    {
      caption: "Dew on the fleece",
      artSrc: "gideon-300/g5-dew-on-fleece.webp",
      lines: [
        "If thou wilt save Israel by mine hand, as thou hast said — let dew be on the fleece only, and dry upon all the earth.",
        "It was so. He wringed a bowl full of water from the fleece.",
      ],
    },
    {
      caption: "But this once",
      artSrc: "gideon-300/g5-but-this-once.webp",
      lines: [
        "Let not thine anger be hot against me… let it now be dry only upon the fleece, and upon all the ground let there be dew.",
        "And God did so that night.",
      ],
    },
  ],
};

export const STORY_THREE_HUNDRED: StageStory = {
  title: "Three Hundred",
  scriptureRef: "Judges 7:1–8",
  lie: "We are too few. This remnant cannot save anyone.",
  reflection:
    "The people are too many — lest Israel vaunt, Mine own hand hath saved me. By the three hundred will I save you.",
  intro: [
    {
      caption: "Too many",
      artSrc: "gideon-300/g6-too-many.webp",
      lines: [
        "The Lord said, The people that are with thee are too many for me to give the Midianites into their hands, lest Israel vaunt themselves against me.",
        "Whosoever is fearful and afraid, let him return. Twenty and two thousand left. Ten thousand remained.",
      ],
    },
    {
      caption: "By the water",
      artSrc: "gideon-300/g6-by-the-water.webp",
      lines: [
        "The Lord tried them at the water. Three hundred lapped, putting hand to mouth.",
        "By the three hundred men that lapped will I save you — and let all the other people go.",
      ],
    },
  ],
};

export const STORY_PITCHERS: StageStory = {
  title: "Sword of the Lord",
  scriptureRef: "Judges 7:9–22",
  lie: "Even now you will fail. Look at the host in the valley.",
  reflection:
    "He asked if God was with them. God answered — and proved it with three hundred against a multitude.",
  intro: [
    {
      caption: "If thou fear",
      artSrc: "gideon-300/g7-if-thou-fear.webp",
      lines: [
        "Arise, get thee down unto the host; for I have delivered it into thine hand.",
        "But if thou fear to go down, go thou with Phurah thy servant — and thou shalt hear what they say.",
      ],
    },
    {
      caption: "The barley cake",
      artSrc: "gideon-300/g7-barley-cake.webp",
      lines: [
        "A man told a dream: a cake of barley bread tumbled into the host of Midian and overturned a tent.",
        "This is nothing else save the sword of Gideon… for into his hand hath God delivered Midian. Gideon worshipped — and returned strengthened.",
      ],
    },
  ],
  outro: [
    {
      caption: "Every man in his place",
      artSrc: "gideon-300/g7-every-man-in-place.webp",
      lines: [
        "Trumpets. Empty pitchers. Lamps within. The sword of the Lord, and of Gideon.",
        "They stood every man in his place. The Lord set every man's sword against his fellow. The host fled.",
      ],
    },
  ],
};
